import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { DropArrowIcon, DropDownCloseIcon, DropDownCheckIcon } from "../../assets/icons";
import { 
  PillContainer, 
  PillContent, 
  PillText, 
  PillArrow, 
  ClearButton,
  DropdownMenu,
  DropdownItem,
  CheckIcon
} from "./PillDropdownStyled";

const PillDropdown = ({
  options = [],
  value,
  defaultValue,
  handleChange,
  placeholder = "Select",
  disabled = false,
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || "");
  const dropdownRef = useRef(null);

  // Update internal state when prop value changes
  useEffect(() => {
    setSelectedValue(value || "");
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionClick = (optionValue) => {
    setSelectedValue(optionValue);
    setIsOpen(false);
    if (handleChange) {
      handleChange(optionValue);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedValue("");
    setIsOpen(false);
    if (handleChange) {
      handleChange("");
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const selectedOption = options.find(option => option.value === selectedValue);
  const displayText = selectedOption?.value ? selectedOption.label : placeholder;
  const hasSelection = selectedValue !== "";

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <PillContainer
        onClick={toggleDropdown}
        disabled={disabled}
        data-testid={testId}
        $isActive={hasSelection || isOpen}
      >
        <PillContent>
          <PillText>{displayText}</PillText>
          <PillArrow isOpen={isOpen}>
            <DropArrowIcon />
          </PillArrow>
          {hasSelection && (
            <ClearButton onClick={handleClear}>
              <DropDownCloseIcon />
            </ClearButton>
          )}
        </PillContent>
      </PillContainer>

      {isOpen && (
        <DropdownMenu>
          {options.map((option) => (
            <DropdownItem
              key={option.value}
              onClick={() => handleOptionClick(option.value)}
              $isSelected={option.value === selectedValue}
            >
              <span>{option.label}</span>
              {option.value === selectedValue && <CheckIcon><DropDownCheckIcon /></CheckIcon>}
            </DropdownItem>
          ))}
        </DropdownMenu>
      )}
    </div>
  );
};

PillDropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  testId: PropTypes.string,
};

export default PillDropdown;
